import { array, number, object, preprocess, string, z } from "zod";

export const FUND_COMPARE_TIME_PERIODS = [
  "1 yr",
  "3 yr",
  "5 yr",
  "10 yr",
] as const;

export type FundCompareTimePeriod = (typeof FUND_COMPARE_TIME_PERIODS)[number];

export const defaultFundCompareBacktest = {
  initial_investment: 1000,
  sip_amount: 1000,
  time_period: "5 yr" as FundCompareTimePeriod,
};

const optionalNumberFromQuery = preprocess((val) => {
  if (val === undefined || val === null || val === "") return undefined;
  const n = Number(val);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}, number().optional());

const timePeriodFromQuery = preprocess((val) => {
  if (val === undefined || val === null) return undefined;
  const s = String(val);
  return (FUND_COMPARE_TIME_PERIODS as readonly string[]).includes(s)
    ? (s as FundCompareTimePeriod)
    : undefined;
}, z.enum(FUND_COMPARE_TIME_PERIODS).optional());

export const stringArrayParam = preprocess((val) => {
  if (val === undefined || val === null) return undefined;
  return Array.isArray(val) ? val : [val];
}, array(string()).optional());

/** Merged into fund compare + PDF report route `validateSearch`. */
export const fundCompareBacktestSearchSchema = object({
  initial_investment: optionalNumberFromQuery,
  sip_amount: optionalNumberFromQuery,
  time_period: timePeriodFromQuery,
  funds: stringArrayParam,
});

const fundIdsCappedParam = preprocess((val) => {
  if (val === undefined || val === null) return undefined;
  const arr = Array.isArray(val) ? val : [val];
  return arr.slice(0, 4);
}, z.array(z.string()).optional());

/**
 * Headless fund compare **report** route only: caps `funds` and applies backtest defaults
 * without a root `.transform()`, so parent `/reports` search (`sections`, `embed`) still
 * type-checks via TanStack Router inheritance.
 */
export const fundCompareSeachSchema = object({
  funds: fundIdsCappedParam,
  initial_investment: optionalNumberFromQuery.transform(
    (n) => n ?? defaultFundCompareBacktest.initial_investment
  ),
  sip_amount: optionalNumberFromQuery.transform(
    (n) => n ?? defaultFundCompareBacktest.sip_amount
  ),
  time_period: timePeriodFromQuery.transform(
    (t) => t ?? defaultFundCompareBacktest.time_period
  ),
});

export function withFundCompareBacktestDefaults(search: {
  initial_investment?: number;
  sip_amount?: number;
  time_period?: FundCompareTimePeriod;
}) {
  return {
    ...search,
    initial_investment:
      search.initial_investment ??
      defaultFundCompareBacktest.initial_investment,
    sip_amount: search.sip_amount ?? defaultFundCompareBacktest.sip_amount,
    time_period: search.time_period ?? defaultFundCompareBacktest.time_period,
  };
}

export type FundCompareBacktestOptions = {
  initialInvestment: number;
  subsequentSIP: number;
  timePeriod: FundCompareTimePeriod;
};

export function toBacktestOptions(search: {
  initial_investment: number;
  sip_amount: number;
  time_period: FundCompareTimePeriod;
}): FundCompareBacktestOptions {
  return {
    initialInvestment: search.initial_investment,
    subsequentSIP: search.sip_amount,
    timePeriod: search.time_period,
  };
}

export function appendBacktestSearchParams(
  params: URLSearchParams,
  options: FundCompareBacktestOptions
) {
  params.set("initial_investment", String(options.initialInvestment));
  params.set("sip_amount", String(options.subsequentSIP));
  params.set("time_period", options.timePeriod);
}
